import { supabase } from "@/utils/supabase";
import { BaseMapper } from "@/adapters/Base.mapper";
/**
 * Concrete implementation of BaseService
 * Handles all database operations for bases
 */
export class BaseService {
    async getBasesWithParams(params) {
        try {
            let query = supabase()
                .from("bases")
                .select("*", { count: "exact" })
                .eq("is_deleted", false);
            if (params.filters) {
                const search = params.filters.search?.trim();
                if (search) {
                    const ors = [
                        `name.ilike.*${search}*`,
                        `addressShort.ilike.*${search}*`,
                        `addressLocality.ilike.*${search}*`,
                    ];
                    query = query.or(ors.join(","));
                }
            }
            if (params.sorters && Object.keys(params.sorters).length > 0) {
                for (const [field, order] of Object.entries(params.sorters)) {
                    if (order === "ascend") {
                        query = query.order(field, { ascending: true });
                    }
                    else if (order === "descend") {
                        query = query.order(field, { ascending: false });
                    }
                }
            }
            else {
                query = query.order("name");
            }
            const from = (params.page - 1) * params.pageSize;
            const to = from + params.pageSize - 1;
            query = query.range(from, to);
            const { data, error, count } = await query;
            if (error) {
                throw error;
            }
            const bases = data.map((dto) => BaseMapper.toDomain(dto));
            return {
                data: bases,
                total: count || 0,
                page: params.page,
                pageSize: params.pageSize,
            };
        }
        catch (error) {
            throw error;
        }
    }
    async getBasesByIds(ids) {
        try {
            const { data, error } = await supabase()
                .from("bases")
                .select("*")
                .in("id", ids)
                .eq("is_deleted", false);
            if (error) {
                throw error;
            }
            return data.map((dto) => BaseMapper.toDomain(dto));
        }
        catch (error) {
            throw error;
        }
    }
    async createBase(base) {
        try {
            const baseDto = BaseMapper.toCreateBaseDto(base);
            const { data, error } = await supabase()
                .from("bases")
                .insert([baseDto])
                .select()
                .single();
            if (error) {
                throw error;
            }
            return BaseMapper.toDomain(data);
        }
        catch (error) {
            throw error;
        }
    }
    async updateBase(id, base) {
        try {
            const baseDto = BaseMapper.toCreateBaseDto(base);
            const { data, error } = await supabase()
                .from("bases")
                .update(baseDto)
                .eq("id", id)
                .select()
                .single();
            if (error) {
                throw error;
            }
            return BaseMapper.toDomain(data);
        }
        catch (error) {
            throw error;
        }
    }
    async deleteBase(id) {
        try {
            const { error } = await supabase()
                .from("bases")
                .update({ is_deleted: true })
                .eq("id", id);
            if (error) {
                throw error;
            }
        }
        catch (error) {
            throw error;
        }
    }
}
// Export singleton instance
export const baseService = new BaseService();
